import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';

// Base container for the list block
const ListBlockContainer = styled.div`
  position: relative;
  margin: 5px 0;
  padding: 0;
  border-radius: 4px;
  background-color: ${props => props.isActive ? 'var(--color-selection-bg, rgba(14, 101, 235, 0.05))' : 'transparent'};
  border: 1px solid ${props => props.isActive ? 'var(--color-selection-border, #dde5f2)' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.isActive ? 'var(--color-selection-bg, rgba(14, 101, 235, 0.05))' : 'var(--color-hover-bg, rgba(0, 0, 0, 0.03))'};
    border-color: ${props => props.isActive ? 'var(--color-selection-border, #dde5f2)' : '#e0e0e0'};
  }

  ${props => props.customStyles}
`;

// List editing container
const ListEditorContainer = styled.div`
  width: 100%;
  white-space: pre-wrap;
  padding: 8px;
  outline: none;
`;

// List view container
const ListViewContainer = styled.div`
  width: 100%;
  padding: 8px;
  cursor: pointer;
  
  ul, ol {
    margin: 0;
    padding-left: 24px;
  }
  
  li {
    padding: 2px 0;
  }
  
  .is-nested-list {
    margin-left: 2.5em;
  }
  
  .has-nested-list {
    margin-bottom: 8px;
  }
`;

// Helper function to process nested lists before rendering
const processMarkdownIndentation = (markdown) => {
  if (!markdown) return '';
  
  const lines = markdown.split('\n');
  
  // Look for list items with indentation
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s+[-*+]/.test(line)) {
      // Already has indentation, leave it as is
    } else if (/^[-*+]/.test(line)) {
      // First level list item, ensure it's at the start
      lines[i] = line.trim();
    }
  }
  
  return lines.join('\n');
};

// Markdown renderers for list items
const markdownRenderers = {
  ul: ({node, ...props}) => {
    // Check if this list is nested 
    const isNested = node?.position?.start?.column > 2;
    return (
      <ul 
        className={`md-list md-ul ${isNested ? 'is-nested-list' : ''}`} 
        data-is-nested={isNested ? 'true' : 'false'}
        style={isNested ? {marginLeft: '2.5em'} : {}}
        {...props} 
      />
    );
  },
  ol: ({node, ...props}) => {
    // Check if this list is nested
    const isNested = node?.position?.start?.column > 2;
    return (
      <ol 
        className={`md-list md-ol ${isNested ? 'is-nested-list' : ''}`} 
        data-is-nested={isNested ? 'true' : 'false'} 
        style={isNested ? {marginLeft: '2.5em'} : {}}
        {...props} 
      />
    );
  },
  li: ({node, ordered, ...props}) => {
    // Check if this list item contains a nested list
    const hasNestedList = props.children && 
      (props.children.some?.(child => 
        typeof child === 'object' && child && 
        (child.type === 'ul' || child.type === 'ol')
      ) || 
      // Also check for arrays of children that might contain lists
      (Array.isArray(props.children) && 
        props.children.some(child => 
          typeof child === 'object' && child && child.props && 
          (child.props.className?.includes('md-list md-ul') || 
           child.props.className?.includes('md-list md-ol'))
        )
      ));
    
    // Check if there's indentation info in the original markdown
    const isNested = node?.position?.start?.column > 2;

    // Calculate the nesting level based on the column position
    const nestLevel = Math.max(0, Math.floor((node?.position?.start?.column || 0) / 3) - 1);
    const nestStyle = nestLevel > 0 ? { paddingLeft: `${nestLevel * 2}em` } : {};
    
    return (
      <li 
        className={`md-list-item ${hasNestedList ? 'has-nested-list' : ''} ${isNested ? 'is-nested' : ''}`}
        data-is-nested={isNested ? 'true' : 'false'}
        data-has-nested-list={hasNestedList ? 'true' : 'false'}
        data-nest-level={nestLevel}
        style={nestStyle}
        {...props} 
      />
    );
  },
  // Simplify paragraph handling
  p: ({node, ...props}) => <p className="md-paragraph" {...props} />
};

/**
 * ListItemBlock component for rendering and editing markdown lists
 * 
 * @param {Object} props Component props
 * @param {boolean} props.isActive Whether the list is active/being edited
 * @param {string|Array} props.content Markdown list content
 * @param {Function} props.onClick Handler for click events
 * @param {Function} props.onContentChange Callback when list content changes
 * @param {Function} props.commitContent Callback to commit content changes
 * @param {Object} props.customStyles Custom styles for the container
 */
const ListItemBlock = React.forwardRef(({
  isActive = false,
  content = '',
  onClick,
  onContentChange,
  commitContent,
  customStyles = {},
  ...otherProps
}, ref) => {
  const editorRef = useRef(null);
  const [initialContent, setInitialContent] = useState(
    Array.isArray(content) ? content.join('\n') : content
  );
  const [isEditing, setIsEditing] = useState(isActive);
  const prevIsActiveRef = useRef(isActive);

  // Update content when it changes from parent
  useEffect(() => {
    const contentStr = Array.isArray(content) ? content.join('\n') : content;
    
    // Only update if not actively editing
    if (!isEditing) {
      setInitialContent(contentStr);
    }
  }, [content, isEditing]);

  // Handle active state changes
  useEffect(() => {
    // Handle becoming active
    if (isActive && !prevIsActiveRef.current) {
      setIsEditing(true);
      
      // Focus the editor when it becomes active
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
          
          // Place cursor at end
          const selection = window.getSelection();
          const range = document.createRange();
          
          if (editorRef.current.lastChild) {
            range.setStartAfter(editorRef.current.lastChild);
          } else {
            range.setStart(editorRef.current, 0);
          }
          
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }, 10);
    }
    
    // Handle becoming inactive
    if (!isActive && prevIsActiveRef.current) {
      // Commit content when going inactive
      if (editorRef.current && commitContent) {
        const currentContent = editorRef.current.innerText;
        if (currentContent !== initialContent) {
          commitContent(currentContent);
          setInitialContent(currentContent);
        }
      }
      setIsEditing(false);
    }
    
    prevIsActiveRef.current = isActive;
  }, [isActive, initialContent, commitContent]);

  // Handle input changes
  const handleInput = useCallback(() => {
    if (editorRef.current && onContentChange) {
      const currentContent = editorRef.current.innerText;
      onContentChange(currentContent);
    }
  }, [onContentChange]);

  // Handle blur events
  const handleBlur = useCallback(() => {
    if (editorRef.current && commitContent) {
      const currentContent = editorRef.current.innerText;
      if (currentContent !== initialContent) {
        commitContent(currentContent);
        setInitialContent(currentContent);
      }
    }
    setIsEditing(false);
  }, [initialContent, commitContent]);

  // Handle key presses
  const handleKeyDown = useCallback((e) => {
    // Handle ESC - revert changes
    if (e.key === 'Escape' && editorRef.current && initialContent !== editorRef.current.innerText) {
      e.preventDefault();
      editorRef.current.innerText = initialContent;
      
      if (onContentChange) {
        onContentChange(initialContent);
      }
      
      setIsEditing(false);
      return;
    }
    
    // Handle Tab in lists - increase indent
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        // Decrease indent
        document.execCommand('outdent', false, null);
      } else {
        // Increase indent
        document.execCommand('indent', false, null);
      }
    }
  }, [initialContent, onContentChange]);

  // Render the component
  return (
    <ListBlockContainer
      isActive={isActive}
      customStyles={customStyles}
      ref={ref}
      {...otherProps}
    >
      {isEditing ? (
        <ListEditorContainer
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          dangerouslySetInnerHTML={{ __html: initialContent }}
          onInput={handleInput}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <ListViewContainer onClick={onClick}>
          <ReactMarkdown components={markdownRenderers}>
            {processMarkdownIndentation(initialContent)}
          </ReactMarkdown>
        </ListViewContainer>
      )}
    </ListBlockContainer>
  );
});

ListItemBlock.displayName = 'ListItemBlock';

export default ListItemBlock;
export { processMarkdownIndentation, markdownRenderers };
